import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSitePosts, createSitePost, updateSitePost, deleteSitePost } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";

interface Post {
  id: number;
  title: string;
  body: string;
  created_at: string;
  updated_at: string;
}

export default function SitePosts() {
  const { siteSlug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formBody, setFormBody] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPost, setDeletingPost] = useState<Post | null>(null);

  // Fetch posts
  const {
    data: posts,
    isLoading,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ["site-posts", siteSlug],
    queryFn: () => getSitePosts(siteSlug as string),
    enabled: !!siteSlug,
  });

  // Create or update post
  const saveMutation = useMutation({
    mutationFn: (data: { title: string; body: string }) => {
      if (editingPost) {
        return updateSitePost(siteSlug as string, editingPost.id, data);
      } else {
        return createSitePost(siteSlug as string, data);
      }
    },
    onSuccess: () => {
      toast.success(editingPost ? "Post updated" : "Post created");
      setDialogOpen(false);
      setEditingPost(null);
      setFormTitle("");
      setFormBody("");
      setFormError(null);
      queryClient.invalidateQueries({ queryKey: ["site-posts", siteSlug] });
    },
    onError: (err: any) => {
      setFormError(err.message || "Failed to save post");
    },
  });

  // Delete post
  const deleteMutation = useMutation({
    mutationFn: (postId: number) => deleteSitePost(siteSlug as string, postId),
    onSuccess: () => {
      toast.success("Post deleted");
      setDeleteDialogOpen(false);
      setDeletingPost(null);
      queryClient.invalidateQueries({ queryKey: ["site-posts", siteSlug] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to delete post");
    },
  });

  // Open create dialog
  const openCreateDialog = () => {
    setEditingPost(null);
    setFormTitle("");
    setFormBody("");
    setFormError(null);
    setDialogOpen(true);
  };

  // Navigate to edit page
  const goToEditPage = (post: Post) => {
    // Prefer slug, fallback to id
    const postSlug = (post as any).slug || post.id;
    navigate(`/sites/${siteSlug}/posts/${postSlug}`);
  };

  // Open delete dialog
  const openDeleteDialog = (post: Post) => {
    setDeletingPost(post);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="max-w-4xl- mx-auto- mt-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Blog Manager</h1>
        <Button onClick={openCreateDialog}>New Article</Button>
      </div>
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-white">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4"><Skeleton className="h-4 w-40" /></td>
                      <td className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                    </tr>
                  ))
                : posts && posts.length > 0 ? (
                    posts.map((post: Post) => (
                      <tr key={post.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">{post.title}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{new Date(post.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-4 flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => goToEditPage(post)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => openDeleteDialog(post)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-6 py-4 text-gray-500" colSpan={3}>
                        No articles found.
                      </td>
                    </tr>
                  )}
            </tbody>
          </table>
        </div>
        {isError && (
          <Alert variant="destructive" className="mt-4">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {(queryError as Error)?.message || "Failed to load posts"}
            </AlertDescription>
          </Alert>
        )}
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPost ? "Edit Article" : "New Article"}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={e => {
              e.preventDefault();
              setFormError(null);
              saveMutation.mutate({ title: formTitle, body: formBody });
            }}
          >
            <Input
              type="text"
              placeholder="Title"
              value={formTitle}
              onChange={e => setFormTitle(e.target.value)}
              required
              className="mb-4"
              autoFocus
            />
            <Textarea
              placeholder="Body"
              value={formBody}
              onChange={e => setFormBody(e.target.value)}
              rows={6}
              required
              className="mb-4"
            />
            {formError && (
              <Alert variant="destructive" className="mb-4">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saveMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saveMutation.isLoading || !formTitle || !formBody}
              >
                {saveMutation.isLoading ? (editingPost ? "Saving..." : "Creating...") : (editingPost ? "Save" : "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Article</DialogTitle>
          </DialogHeader>
          <div className="mb-4">
            Are you sure you want to delete <span className="font-semibold">{deletingPost?.title}</span>?
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deletingPost && deleteMutation.mutate(deletingPost.id)}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
